/**
 * useMarketData.ts — v9.0
 *
 * Theta-only platform runtime.
 *
 * Market data flows exclusively through MER API → Theta Gateway service.
 * Route prefix: /api/market-data/*
 * Provider selection is NOT a user-configurable setting.
 *
 * Three data modes:
 * 1. SUB-MINUTE / TICK  — WebSocket tick aggregation into 1s/5s/15s/30s/tick candles
 * 2. STANDARD (1m/5m/15m) — REST history + live-candle updates
 * 3. MANUAL — one-shot button press
 *
 * Exposes rich telemetry: wsStatus, lastTickTs, lastFetchTs, lastPollTs, pollIntervalSec
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useStore } from '../../store';
import { fetchIntraday } from '../../data/thetaDataService';
import { Candle } from '../../types';

// ─── Interval utilities ───────────────────────────────────────────────────────

export type IntervalOption =
  'tick' | '1sec' | '5sec' | '15sec' | '30sec' | '1min' | '5min' | '15min' | '1h' | '4h' | '1D' | '1W';

/**
 * The set of chart intervals that the API can actually serve today.
 * Phase 3 widened DataState['interval'] to also include '1h' | '4h' | '1D'
 * | '1W' for the UI selector; position 15 enabled '1h' (native), '4h'
 * (server-derived from native 1h), '1D' (finalized-day provider EOD,
 * p15-1d-eod-v1 — no active daily bar), and '1W' (finalized-week
 * composition from canonical 1D, p15-1w-cal-v1 — no active weekly bar) as
 * fetchable — the full canonical selector universe is now servable.
 *
 * Use this set / helper to narrow a DataState['interval'] before calling
 * any fetch path.
 */
const FETCHABLE_INTERVALS: ReadonlySet<string> = new Set([
  'tick', '1sec', '5sec', '15sec', '30sec', '1min', '5min', '15min', '1h', '4h', '1D', '1W',
]);

export function isFetchableInterval(s: string | undefined | null): s is IntervalOption {
  return !!s && FETCHABLE_INTERVALS.has(s);
}

/**
 * Narrow a DataState['interval'] value to a fetchable IntervalOption, or null
 * when the value is not fetchable today (G-CLIENT-2 / D-GCLIENT2-2a).
 *
 * A non-fetchable canonical timeframe (1D/1W before their position-15
 * gates) is
 * NEVER silently substituted with 5min or any other served interval: callers
 * must treat null as an explicit non-fetching outcome and issue no request.
 * The user's selection itself is left untouched.
 */
export function toFetchableInterval(
  s: string | undefined | null,
): IntervalOption | null {
  return isFetchableInterval(s) ? s : null;
}

export function bucketMs(interval: IntervalOption): number {
  // '1D' and '1W' are 0 (nominal identity only): finalized-day/-week bars
  // have no live bucket (ADR §67/§69 — no active daily or weekly candle),
  // the banked runtime audit proved bucketMs has no REST-interval runtime
  // consumer to feed, and an ET calendar week is not a fixed duration
  // (604800000 is prohibited).
  const map: Record<IntervalOption, number> = {
    'tick': 0, '1sec': 1000, '5sec': 5000, '15sec': 15000, '30sec': 30000,
    '1min': 60000, '5min': 300000, '15min': 900000, '1h': 3600000, '4h': 14400000, '1D': 0, '1W': 0,
  };
  return map[interval] ?? 60000;
}

export function isSubMinute(interval: IntervalOption): boolean {
  return ['tick','1sec','5sec','15sec','30sec'].includes(interval);
}

function historyInterval(interval: IntervalOption): '1min'|'5min'|'15min'|'1h'|'4h'|'1D'|'1W' {
  if (isSubMinute(interval)) return '1min';
  return interval as '1min'|'5min'|'15min'|'1h'|'4h'|'1D'|'1W';
}

// ─── Market hours ─────────────────────────────────────────────────────────────

export function isMarketOpen(): boolean {
  const now = new Date();
  const day = now.getDay();
  if (day === 0 || day === 6) return false;
  const etMin = ((now.getUTCHours() * 60 + now.getUTCMinutes()) - 4*60 + 1440) % 1440;
  return etMin >= 9*60+30 && etMin < 16*60;
}

/**
 * Stage 14 (2026-04-27): Extended-hours data window.
 *
 * Returns true during the full window that ThetaData (and US equity feeds in
 * general) can deliver fresh quote/bar data: pre-market 4:00 AM – 9:30 AM ET,
 * regular hours 9:30 AM – 4:00 PM ET, and after-hours 4:00 PM – 8:00 PM ET.
 *
 * Use this — NOT isMarketOpen() — to gate data-refresh logic. Otherwise the
 * watchlist freezes during pre/post sessions even though live quotes are
 * available.
 *
 * isMarketOpen() retains its original "regular hours only" semantics so that
 * AI prompts and UI status indicators continue to distinguish RTH from EH.
 */
export function isMarketDataAvailable(): boolean {
  const now = new Date();
  const day = now.getDay();
  if (day === 0 || day === 6) return false;
  const etMin = ((now.getUTCHours() * 60 + now.getUTCMinutes()) - 4*60 + 1440) % 1440;
  // 4:00 AM (240) through 8:00 PM (1200) ET, weekdays only
  return etMin >= 4*60 && etMin < 20*60;
}

// ─── Candle merge ─────────────────────────────────────────────────────────────

function mergeCandles(existing: Candle[], fresh: Candle[]): Candle[] {
  if (!existing.length) return fresh;
  if (!fresh.length) return existing;
  const map = new Map<number,Candle>();
  existing.forEach(c => map.set(c.ts, c));
  fresh.forEach(c => map.set(c.ts, c));
  return Array.from(map.values()).sort((a,b) => a.ts - b.ts);
}

// ─── WS connection status (exported for UI) ───────────────────────────────────

export type WSConnectionStatus = 'disconnected'|'connecting'|'connected'|'reconnecting'|'error';

export type DataFeedStatus =
  | 'idle' | 'fetching' | 'live' | 'live-tick'
  | 'polling' | 'paused' | 'error' | 'market-closed';

// ─── Singleton telemetry store (module-level, not React state) ────────────────
// These are updated on every tick/fetch so components can read them via the hook.

interface FeedTelemetry {
  wsStatus:        WSConnectionStatus;
  lastTickTs:      number | null;   // ms — last WS tick received
  lastFetchTs:     number | null;   // ms — last REST fetch completed
  lastPollTs:      number | null;   // ms — last background poll
  pollIntervalSec: number;
  provider:        string;
  tickCount:       number;          // total ticks received this session
}

const telemetry: FeedTelemetry = {
  wsStatus: 'disconnected',
  lastTickTs: null,
  lastFetchTs: null,
  lastPollTs: null,
  pollIntervalSec: 60,
  provider: 'none',
  tickCount: 0,
};

// Listeners so the hook can re-render when telemetry changes
const telemetryListeners = new Set<() => void>();
function notifyTelemetry() { telemetryListeners.forEach(fn => fn()); }

function updateTelemetry(patch: Partial<FeedTelemetry>) {
  Object.assign(telemetry, patch);
  notifyTelemetry();
}

export function useTelemetry(): FeedTelemetry {
  const [, rerender] = useState(0);
  useEffect(() => {
    const fn = () => rerender(n => n + 1);
    telemetryListeners.add(fn);
    return () => { telemetryListeners.delete(fn); };
  }, []);
  return telemetry;
}

// ─── TwelveData WebSocket manager ────────────────────────────────────────────


// ─── Main hook ────────────────────────────────────────────────────────────────

export function useMarketData() {
  const { settings, watchlist, activeSymbol, symbolData,
          setCandles, setLoading, setError, setMeta, setProvenance,
          setProviderConfidence } = useStore();

  const inflightRef = useRef(new Set<string>());
  const pollTimer   = useRef<ReturnType<typeof setInterval> | null>(null);

  const [feedStatus, setFeedStatus] = useState<DataFeedStatus>('idle');

  // ── Effective interval ──────────────────────────────────────────────────────
  const getSymInterval = useCallback((sym: string): IntervalOption => {
    const stored = useStore.getState().symbolData[sym]?.interval as IntervalOption | undefined;
    return stored || (settings.defaultInterval as IntervalOption) || '1sec';
  }, [settings.defaultInterval]);

  // ── REST fetch ──────────────────────────────────────────────────────────────
  const fetchSymbol = useCallback(async (
    symbol: string,
    interval?: IntervalOption,
    silent = false
  ) => {
    const sym = symbol.toUpperCase();
    const ivl: IntervalOption = interval || getSymInterval(sym);

    if (inflightRef.current.has(sym)) return;
    inflightRef.current.add(sym);

    // P3 visible provider trust: do NOT assert a provider before anything has
    // served. The served provenance below is the only truthful source, so the
    // telemetry provider is written after the response, not guessed before it.
    if (!silent) { setLoading(sym, true); setError(sym, null); setFeedStatus('fetching'); }

    const restIvl = historyInterval(ivl);
    const result  = await fetchIntraday(sym, restIvl);
    inflightRef.current.delete(sym);

    const now = Date.now();

    if (result.error) {
      // Not attributed to a provider: at this point which one failed is exactly
      // what is not known, and naming Theta was false whenever Polygon served.
      if (!silent) { setError(sym, `Market data: ${result.error}`); setFeedStatus('error'); }
      return;
    }
    const bars = Array.isArray(result.candles) ? result.candles : [];
    if (!bars.length) {
      if (!silent) { setError(sym, `No data for ${sym}. Check symbol or market hours.`); setFeedStatus('error'); }
      return;
    }

    const existing = useStore.getState().symbolData[sym]?.candles || [];
    const merged = (silent && existing.length) ? mergeCandles(existing, bars) : bars;

    setCandles(sym, merged, 'api');
    if (Object.keys(result.meta).length) setMeta(sym, result.meta);
    // Governed provenance, stored as a sibling of `meta`. `null` is recorded
    // rather than skipped so a later unknown cannot read as a stale provider.
    setProvenance(sym, result.provenance ?? null);
    setProviderConfidence(sym, result.providerConfidence ?? null);

    updateTelemetry({
      lastFetchTs: now,
      ...(result.provenance?.provider ? { provider: result.provenance.provider } : {}),
      ...(silent ? { lastPollTs: now } : {}),
    });

    // Clear any stale error chip on successful fetch — for silent polls this
    // lets the watchlist chip self-heal in the background when a symbol that
    // previously failed (e.g. manual refresh during upstream blip) recovers.
    // Without this, chips set by failed non-silent fetches stick forever until
    // the user manually refreshes the symbol again. We deliberately do NOT
    // set the chip on silent failures — chips only appear when the user
    // deliberately tried to refresh and got an error, which is a stronger
    // signal than transient background polling noise.
    setError(sym, null);

    if (!silent) {
      setFeedStatus(settings.autoRefresh ? (isSubMinute(ivl) ? 'live-tick' : 'polling') : 'idle');
    }
  }, [settings, getSymInterval, setCandles, setLoading, setError, setMeta, setProvenance,
      setProviderConfidence]);

  // ── Start polling ───────────────────────────────────────────────────────────
  const startPolling = useCallback(() => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    if (!settings.autoRefresh) return;
    // Platform lock: Theta backend proxy requires no client-side apiKey
    updateTelemetry({ pollIntervalSec: settings.refreshIntervalSec });
    pollTimer.current = setInterval(async () => {
      // Stage 14: gate on the wider data-available window (4 AM – 8 PM ET)
      // rather than strict RTH. isMarketOpen() stays narrow for AI/UI semantics;
      // isMarketDataAvailable() gates data fetches.
      if (!isMarketDataAvailable()) { setFeedStatus('market-closed'); return; }
      setFeedStatus('polling');
      const sym = useStore.getState().activeSymbol;
      if (sym) {
        const ivl = getSymInterval(sym);
        await fetchSymbol(sym, ivl, true);
      }
    }, settings.refreshIntervalSec * 1000);
  }, [settings, getSymInterval, fetchSymbol]);

  // ── Bootstrap ───────────────────────────────────────────────────────────────
  useEffect(() => {
    // No provider is asserted at bootstrap — nothing has served yet.
    // The backend proxy is active when autoRefresh is on.
    if (settings.autoRefresh) startPolling();
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.autoRefresh, settings.refreshIntervalSec]);

  // ── Auto-fetch on symbol switch ─────────────────────────────────────────────
  useEffect(() => {
    if (!activeSymbol || !settings.autoFetchOnSymbolSwitch) return;
    const symData = symbolData[activeSymbol];
    const stale = !symData?.candles?.length
      || symData.source === 'sample'
      || (symData.lastUpdated && Date.now() - symData.lastUpdated > 2*60*1000);
    if (stale) fetchSymbol(activeSymbol, getSymInterval(activeSymbol), false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSymbol]);

  // ── Auto-fetch any watchlist symbol missing candles ─────────────────────────
  useEffect(() => {
    if (!settings.autoFetchOnSymbolSwitch) return;
    watchlist.forEach((w: { symbol: string }) => {
      const sd = symbolData[w.symbol];
      if (!sd?.candles?.length && !sd?.loading) {
        fetchSymbol(w.symbol, getSymInterval(w.symbol), true);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchlist.length, activeSymbol]);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, []);

  return { fetchSymbol, feedStatus };
}
